import styled from '@emotion/styled';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FontFamily } from '@/enums/index';
import { IMeetingRequestAttributes } from '@/interfaces/events/IMeetingRequestAttributes';
import { ButtonVariant } from '@/styles/themes/button';

import { FilledButton } from './core/Button/variants/FilledButton';
import { ErrorMessage } from './core/inputs/ErrorMessage';
import { Field } from './core/inputs/Field';
import { TextArea } from './core/inputs/TextArea';

const Container = styled.div`
  padding: 60px 40px;
`;

const Heading = styled.h2`
  font-size: 32px;
  line-height: 40px;
  font-family: ${FontFamily.SpaceGrotesk};
  margin: 0 0 32px 0;
`;

interface IRequestMeetingFormProps {
  onSubmit: (formData: IMeetingRequestAttributes) => void;
}

export const RequestMeetingForm: FC<IRequestMeetingFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  return (
    <Container>
      <Heading>{t('expo.sponsor.requestMeetingForm.title')}</Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div css={{ marginBottom: 22 }}>
          <Field
            {...register('name', {
              required: { value: true, message: t('expo.sponsor.requestMeetingForm.requiredName') },
            })}
            label={t('expo.sponsor.requestMeetingForm.nameField')}
            hasValue={Boolean(watch('name'))}
            isError={Boolean(errors['name'])}
            autoComplete="name"
          />
          <ErrorMessage errors={errors} name="name" />
        </div>
        <div css={{ marginBottom: 32 }}>
          <Field
            {...register('email', {
              required: {
                value: true,
                message: t('expo.sponsor.requestMeetingForm.requiredEmail'),
              },
            })}
            label={t('expo.sponsor.requestMeetingForm.emailField')}
            hasValue={Boolean(watch('email'))}
            isError={Boolean(errors['email'])}
            autoComplete="email"
          />
          <ErrorMessage errors={errors} name="email" />
        </div>
        <div css={{ marginBottom: 66 }}>
          <TextArea {...register('message')} label={t('expo.sponsor.requestMeetingForm.messageField')} rows={5} />
        </div>
        <FilledButton css={{ paddingTop: 12, paddingBottom: 12 }} variant={ButtonVariant.DARK_FILLED} type="submit">
          {t('expo.sponsor.requestMeetingForm.submit')}
        </FilledButton>
      </form>
    </Container>
  );
};
